import { Button, Card, Typography } from "@material-tailwind/react";
import React from "react";
import DatePicker from "react-datepicker";
import { HOURLY_BURNER_RUN_TIME_REPORT_HEAD } from "../../variables/constants";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

function HourlyBurnerReport(props) {
  const getTimeIntervals = (hours) => {
    let startTimes = [];
    let endTimes = [];
    for (let i = 0; i < hours; i++) {
      const start = i < 10 ? `0${i}:00` : `${i}:00`;
      const end = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
      startTimes.push(start);
      endTimes.push(end);
    }
    return { startTimes, endTimes };
  };

  const { startTimes, endTimes } = getTimeIntervals(24);

  const handleReportDownload = () => {
    const data = startTimes.map((item, i) => {
      return [
        i + 1,
        startTimes[i] ?? "",
        endTimes[i] ?? "",
        props.hourlyBurnerDetails?.burner1
          ? props.hourlyBurnerDetails?.burner1[i] > 0
            ? formatTime(props.hourlyBurnerDetails?.burner1[i])
            : formatTime(props.hourlyBurnerDetails?.burner1[i]) ?? "-"
          : "",
        props.hourlyBurnerDetails?.burner2
          ? props.hourlyBurnerDetails?.burner2[i] > 0
            ? formatTime(props.hourlyBurnerDetails?.burner2[i])
            : formatTime(props.hourlyBurnerDetails?.burner2[i]) ?? "-"
          : "",
        props.hourlyBurnerDetails?.burner3
          ? props.hourlyBurnerDetails?.burner3[i] > 0
            ? formatTime(props.hourlyBurnerDetails?.burner3[i])
            : formatTime(props.hourlyBurnerDetails?.burner3[i]) ?? "-"
          : "",
        props.hourlyBurnerDetails?.burner4
          ? props.hourlyBurnerDetails?.burner4[i] > 0
            ? formatTime(props.hourlyBurnerDetails?.burner4[i])
            : formatTime(props.hourlyBurnerDetails?.burner4[i]) ?? "-"
          : "",
      ];
    });

    // Create a worksheet
    const ws = XLSX.utils.aoa_to_sheet([
      HOURLY_BURNER_RUN_TIME_REPORT_HEAD,
      ...data,
    ]);

    // Create a new workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hourly Burner Run Time Report");

    // Convert workbook to binary string
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Save the file
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "hourly_burner_run_time_report.xlsx"
    );
  };

  const formatTime = (totalMinutes) => {
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    const seconds = "00"; // Since we're dealing with minutes, seconds would be zero

    return `${hours}:${minutes}:${seconds}`;
  };

  return (
    <div>
      {props.hourlyBurnerDetails ? (
        <div className="mt-5">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-5 font-bold text-2xl text-[#BD3B24]">
            <div>
              <p className="text-2xl font-bold text-black">
                Hourly Burner Run Time Report
              </p>
              <p className="text-black font-semibold text-2xl">
                Branch: {props.hourlyBurnerDetails?.branch_name}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-end sm:items-center justify-end md:justify-[unset] gap-3 sm:gap-0">
              {props.hourlyBurnerDetails && (
                <Button
                  onClick={handleReportDownload}
                  className="font-poppins font-medium tracking-wider bg-[#03438D]"
                >
                  Download
                </Button>
              )}
              <DatePicker
                selected={props.filterDate}
                onChange={props.handleFilterDateChange}
                value={props.filterDate}
                maxDate={new Date()}
                dateFormat={"dd/MM/YYYY"}
                // isClearable
                placeholderText="Select a Date"
                className="bg-[#F5F7F9] ml-4 px-5 py-2 outline-none border-none rounded-lg drop-shadow-lg font-normal text-base"
              />
            </div>
          </div>

          <Card
            className="h-full w-full rounded-none overflow-x-scroll md:overflow-x-hidden mt-4"
            style={{
              boxShadow:
                "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.06)",
            }}
          >
            <table className="w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  {HOURLY_BURNER_RUN_TIME_REPORT_HEAD.map((head) => (
                    <th
                      key={head}
                      className="border-b border-blue-gray-100 bg-[#03438D] p-4"
                    >
                      <Typography
                        variant="small"
                        color="blue-gray"
                        className="font-poppins text-white uppercase font-normal tracking-wider"
                      >
                        {head}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {startTimes &&
                  startTimes.map((item, index) => {
                    return (
                      <tr key={index}>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {index + 1}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {startTimes[index] ?? ""}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className={`font-poppins font-medium w-fit`}
                          >
                            {endTimes[index] ?? ""}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {props.hourlyBurnerDetails?.burner1
                              ? props.hourlyBurnerDetails?.burner1[index] > 0
                                ? formatTime(
                                    props.hourlyBurnerDetails?.burner1[index]
                                  )
                                : formatTime(
                                    props.hourlyBurnerDetails?.burner1[index]
                                  ) ?? "-"
                              : ""}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {props.hourlyBurnerDetails?.burner2
                              ? props.hourlyBurnerDetails?.burner2[index] > 0
                                ? formatTime(
                                    props.hourlyBurnerDetails?.burner2[index]
                                  )
                                : formatTime(
                                    props.hourlyBurnerDetails?.burner2[index]
                                  ) ?? "-"
                              : ""}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {props.hourlyBurnerDetails?.burner3
                              ? props.hourlyBurnerDetails?.burner3[index] > 0
                                ? formatTime(
                                    props.hourlyBurnerDetails?.burner3[index]
                                  )
                                : formatTime(
                                    props.hourlyBurnerDetails?.burner3[index]
                                  ) ?? "-"
                              : ""}
                          </Typography>
                        </td>
                        <td className="p-4">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins font-medium"
                          >
                            {props.hourlyBurnerDetails?.burner4
                              ? props.hourlyBurnerDetails?.burner4[index] > 0
                                ? formatTime(
                                    props.hourlyBurnerDetails?.burner4[index]
                                  )
                                : formatTime(
                                    props.hourlyBurnerDetails?.burner4[index]
                                  ) ?? "-"
                              : ""}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })}

                <tr className="border-t-[2px] border-[#03438D]">
                  <td className="p-4 text-center" colSpan={3}>
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="font-poppins font-bold"
                    >
                      Total Hours
                    </Typography>
                  </td>
                  <td className="p-4">
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="font-poppins font-bold"
                    >
                      {props.hourlyBurnerDetails.totalBurnerMinutes
                        ? props.hourlyBurnerDetails.totalBurnerMinutes[1] > 0
                          ? formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[1]
                            )
                          : formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[1]
                            )
                        : ""}
                    </Typography>
                  </td>
                  <td className="p-4">
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="font-poppins font-bold"
                    >
                      {props.hourlyBurnerDetails.totalBurnerMinutes
                        ? props.hourlyBurnerDetails.totalBurnerMinutes[2] > 0
                          ? formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[2]
                            )
                          : formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[2]
                            )
                        : ""}
                    </Typography>
                  </td>
                  <td className="p-4">
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="font-poppins font-bold"
                    >
                      {props.hourlyBurnerDetails.totalBurnerMinutes
                        ? props.hourlyBurnerDetails.totalBurnerMinutes[3] > 0
                          ? formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[3]
                            )
                          : formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[3]
                            )
                        : ""}
                    </Typography>
                  </td>
                  <td className="p-4">
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="font-poppins font-bold"
                    >
                      {props.hourlyBurnerDetails.totalBurnerMinutes
                        ? props.hourlyBurnerDetails.totalBurnerMinutes[4] > 0
                          ? formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[4]
                            )
                          : formatTime(
                              props.hourlyBurnerDetails.totalBurnerMinutes[4]
                            )
                        : ""}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      ) : (
        <div className="text-center h-[50vh] flex flex-col justify-center items-center text-black">
          <p className="font-semibold text-xl sm:text-3xl">
            Oops! No Data Found...
          </p>
          <p className="text-sm mt-2 ">
            Looks like we couldn't find any data that match your criteria right
            now.
          </p>
        </div>
      )}
    </div>
  );
}

export default HourlyBurnerReport;
